import { useEffect, useState } from "react";

import classNames from "classnames";
import { hyphenate } from "neetocist";
import useStateWithDependency from "neetocommons/react-utils/useStateWithDependency";
import { LeftArrow } from "neetoicons";
import { Button, Typography } from "neetoui";
import { mergeLeft } from "ramda";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Container from "components/Container";
import Header from "components/Header";

import Code from "./Code";
import {
  EMBED_CUSTOMIZATION_COMPONENTS,
  EMBED_TITLES,
  SCRIPT_ID,
} from "./constants";
import Preview from "./Preview";
import "./product-embed.scss";
import { buildDefaultCustomization } from "./utils";

const EmbedRenderer = ({
  className,
  customEmbedScriptPath,
  customPreviewIframeUrl,
  id,
  options,
  extraArgs,
  otherCustomizations,
  inlineWrapperStyle,
  iframeStyle,
  supportedLanguages,
  selectedEmbed,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [isScriptLoading, setIsScriptLoading] = useState(true);

  const [customizations, setCustomizations] = useStateWithDependency(
    () => buildDefaultCustomization(options),
    [options?.iframe?.iframeUrl]
  );

  const { description } = customizations[selectedEmbed];
  const customization = customizations[selectedEmbed];
  const CustomizationComponent = EMBED_CUSTOMIZATION_COMPONENTS[selectedEmbed];

  const embedScriptLink =
    customEmbedScriptPath ||
    `${window.location.protocol}//${window.location.host}/javascript/embed.js`;

  const loadScript = () => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = embedScriptLink;
    script.onload = () => setIsScriptLoading(false);
    document.head.appendChild(script);
  };

  const updateCustomization = props =>
    setCustomizations(prevState => ({
      ...prevState,
      [selectedEmbed]: mergeLeft(props, prevState[selectedEmbed]),
    }));

  useEffect(() => {
    const isScriptLoaded = document.getElementById(SCRIPT_ID);
    if (isScriptLoaded) setIsScriptLoading(false);
    else loadScript();
  }, []);

  return (
    <Container
      data-testid="embed-product"
      className={classNames(
        "neeto-molecules-embed flex w-full flex-grow flex-col p-0 md:flex-row",
        className
      )}
    >
      <div className="neeto-molecules-embed__left-sidebar px-5 py-8 md:h-full md:overflow-y-auto md:px-10">
        <Button
          className="mb-4"
          data-testid="back-to-embed-selection-button"
          icon={LeftArrow}
          iconPosition="left"
          label={t("neetoMolecules.productEmbed.backToEmbedSelection")}
          size="small"
          style="link"
          to={location.pathname}
        />
        <Header
          className="py-0"
          data-testid={`${hyphenate(selectedEmbed)}-embed-title`}
          title={EMBED_TITLES[selectedEmbed]}
        />
        {description && (
          <Typography
            className="mt-2"
            data-testid={`${hyphenate(selectedEmbed)}-description`}
            style="body2"
          >
            {description}
          </Typography>
        )}
        <div className="mt-4 flex w-full flex-col gap-y-6">
          <div className="flex flex-col gap-y-6">
            <Code
              {...{
                customization,
                embedScriptLink,
                extraArgs,
                id,
                iframeStyle,
                inlineWrapperStyle,
                selectedEmbed,
                supportedLanguages,
              }}
            />
            <CustomizationComponent
              {...{
                customization,
                otherCustomizations,
                updateCustomization,
              }}
            />
          </div>
        </div>
      </div>
      <div className="neeto-molecules-embed__preview neeto-ui-bg-gray-50 flex min-h-dvh w-full min-w-0 flex-col gap-3 overflow-y-auto p-6 md:h-full md:min-h-0 md:flex-grow">
        <Preview
          {...{
            customPreviewIframeUrl,
            customization,
            id,
            isScriptLoading,
            selectedEmbed,
          }}
        />
      </div>
    </Container>
  );
};

export default EmbedRenderer;
